import React, { useState } from 'react';
import { Box, Grid, Divider, useMediaQuery, useTheme } from '@material-ui/core';
import ChevronCardButtonList from 'components/ChevronCardButtonList';
import InputField from 'components/InputField';
import PhoneMaskInput from 'components/InputField/PhoneMaskInput';
import { LongBody, ShortBody } from 'components/Typography';
import {
  StyledPaper,
  StyledDiv,
  StyledLongBody,
  StyledShortBody,
} from 'containers/MyAccountPage/styles';
import get from 'lodash/get';
import { AssuranceIcon } from 'images';
import { sanlamVeryLightgray, sanlamDarkGrayishBlue } from 'Material-UI/colors';
import CardHeader from 'containers/MyAccountPage/CardHeader';
import { injectIntl, IntlShape } from 'react-intl';
import messages from 'containers/MyAccountPage/messages';
import { translate } from 'helpers/intl';
import { capitalize } from 'lodash';
import ChangePassewordModal from 'components/ChangePassewordModal';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  getIsLoadingChangePasswordSelector,
  getIsLoadingChangeSecretQuestionSelector,
} from 'store/credentials/selectors';
import {
  changePassword,
  changeSecretQuestion,
} from 'store/credentials/actions';
import ChangeSecretQuestionModal from 'components/ChangeSecretQuestionModal';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { EVENT_CATEGORIES } from 'helpers/GATracking';

const PersonalInfo = (props: PersonalInfoType) => {
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(
    false,
  );
  const [isChangeSecretQuestionOpen, setIsChangeSecretQuestionOpen] = useState(
    false,
  );

  const items = [
    {
      title: (
        <GaClickHOC
          category={EVENT_CATEGORIES.SETTINGS}
          label="Changer mon mot de passe"
        >
          <StyledShortBody color={sanlamDarkGrayishBlue} weight="500">
            {translate(get(messages, 'changeMyPassword'), props)}
          </StyledShortBody>
        </GaClickHOC>
      ),
      icon: 'lockIcon',
      action: /* istanbul ignore next */ () => {
        setIsChangePasswordModalOpen(true);
      },
    },
    {
      title: (
        <GaClickHOC
          category={EVENT_CATEGORIES.SETTINGS}
          label="Changer ma question secrète"
        >
          <StyledShortBody color={sanlamDarkGrayishBlue} weight="500">
            {translate(get(messages, 'changeMySecretQuestion'), props)}
          </StyledShortBody>
        </GaClickHOC>
      ),
      icon: 'messagesIcon',
      action: /* istanbul ignore next */ () => {
        setIsChangeSecretQuestionOpen(true);
      },
    },
  ];
  const { values, touched, errors, handleChange, handleBlur } = props;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <>
      <CardHeader
        title={translate(get(messages, 'myPersonalInfo'), props)}
        icon="blueUser"
      />
      <StyledPaper>
        <Box pt={4} px={3} pb={3}>
          <Box mb={3}>
            <LongBody>{translate(get(messages, 'myProfile'), props)}</LongBody>
          </Box>
          <Grid container>
            <Grid container item xs={matches ? 12 : 5}>
              <Grid item xs={3}>
                <StyledDiv>
                  <img
                    height="24px"
                    width="24px"
                    src={get(AssuranceIcon, 'blueUser')}
                    alt="profile-icon"
                  />
                </StyledDiv>
              </Grid>
              <Grid item xs={9}>
                <StyledLongBody color="#0880BC" weight="400">
                  {capitalize(props.firstName)} {capitalize(props.lastName)}
                </StyledLongBody>
                <ShortBody>
                  {translate(get(messages, 'cin'), props)}
                  <strong>{props.cin}</strong>
                </ShortBody>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={matches ? 12 : 6}>
                <InputField
                  name="birthday"
                  label={translate(get(messages, 'birthDay'), props)}
                  type="date"
                  error={touched.birthday && errors.birthday}
                  helperText={touched.birthday && errors.birthday}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={values.birthday}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    inputProps: { max: '9999-12-30' },
                  }}
                />
              </Grid>
              <Grid item xs={matches ? 12 : 6}>
                <InputField
                  name="phoneNumber"
                  label={translate(get(messages, 'phoneNumber'), props)}
                  placeholder={translate(get(messages, 'phoneNumber'), props)}
                  error={touched.phoneNumber && errors.phoneNumber}
                  helperText={touched.phoneNumber && errors.phoneNumber}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={values.phoneNumber.substring(1)}
                  InputProps={{
                    inputComponent: PhoneMaskInput,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  name="email"
                  label={translate(get(messages, 'email'), props)}
                  placeholder={translate(get(messages, 'email'), props)}
                  error={touched.email && errors.email}
                  helperText={touched.email && errors.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={values.email}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Divider />
        <Box pt={2} px={3} pb={4}>
          <LongBody>
            {translate(get(messages, 'securitySettings'), props)}
          </LongBody>
          <ChevronCardButtonList
            borderColor={sanlamVeryLightgray}
            items={items}
            spacing={2}
            deleteHoverBorder={true}
            direction="column"
          />
        </Box>
      </StyledPaper>
      <ChangePassewordModal
        isOpen={isChangePasswordModalOpen}
        closeAddNewContractModal={() => {
          setIsChangePasswordModalOpen(false);
        }}
        changePassword={props.changePassword}
        isLoading={props.isLoadingChangePassword}
      />
      <ChangeSecretQuestionModal
        isOpen={isChangeSecretQuestionOpen}
        closeModal={
          /* istanbul ignore next */ () => {
            setIsChangeSecretQuestionOpen(false);
          }
        }
        changeSecretQuestion={props.changeSecretQuestion}
        isLoading={props.isLoadingChangeSecretQuestion}
      />
    </>
  );
};
export interface PersonalInfoType {
  intl: IntlShape;
  firstName: string;
  lastName: string;
  cin: string;
  changePassword: Function;
  isLoadingChangePassword: boolean;
  changeSecretQuestion: Function;
  isLoadingChangeSecretQuestion: boolean;
  values: any;
  touched: any;
  errors: any;
  handleChange: Function;
  handleBlur: Function;
}

const mapStateToProps = createStructuredSelector<
  any,
  {
    isLoadingChangePassword: boolean;
    isLoadingChangeSecretQuestion: boolean;
  }
>({
  isLoadingChangePassword: getIsLoadingChangePasswordSelector,
  isLoadingChangeSecretQuestion: getIsLoadingChangeSecretQuestionSelector,
});

const mapDispatchToProps = {
  changePassword,
  changeSecretQuestion,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(PersonalInfo));
